import { useCallback, useEffect, useState } from "react";

/** A partir daqui a barra lateral fica fixa na tela; abaixo, é uma gaveta (`lg` do Tailwind). */
export const DESKTOP_QUERY = "(min-width: 1024px)";
export const SIDEBAR_COLLAPSED_KEY = "prodgy.sidebar.collapsed";

type PreferenceStore = Pick<Storage, "getItem" | "setItem">;

/** Lê a preferência salva. Sem armazenamento (aba privada, bloqueio), a barra abre completa. */
export function readCollapsedPreference(store: PreferenceStore | null | undefined): boolean {
  try {
    return store?.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

/** Grava a preferência; falhar só faz a escolha valer até recarregar a página. */
export function writeCollapsedPreference(store: PreferenceStore | null | undefined, collapsed: boolean): void {
  try {
    store?.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  } catch {
    // A escolha segue valendo nesta sessão, no estado do React.
  }
}

function browserStore(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function matchesDesktop(): boolean {
  return typeof window !== "undefined" && window.matchMedia(DESKTOP_QUERY).matches;
}

/**
 * Estado da barra lateral. No desktop ela nunca some: o botão de menu alterna
 * entre completa e recolhida (só os ícones), e a escolha fica salva. No celular
 * ela é uma gaveta que abre por cima do conteúdo e começa fechada.
 */
export function useSidebarState() {
  const [isDesktop, setIsDesktop] = useState(matchesDesktop);
  const [collapsed, setCollapsed] = useState(() => readCollapsedPreference(browserStore()));
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const query = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => {
      setIsDesktop(query.matches);
      setMobileOpen(false);
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const toggle = useCallback(() => {
    if (!isDesktop) {
      setMobileOpen((open) => !open);
      return;
    }
    setCollapsed((current) => {
      writeCollapsedPreference(browserStore(), !current);
      return !current;
    });
  }, [isDesktop]);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  return {
    isDesktop,
    /** Desktop: a barra está recolhida em ícones. */
    collapsed: isDesktop && collapsed,
    /** Celular: a gaveta está aberta. */
    mobileOpen,
    /** A barra mostra os nomes dos itens agora — completa no desktop ou gaveta aberta. */
    expanded: isDesktop ? !collapsed : mobileOpen,
    toggle,
    closeMobile,
  };
}
